import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../hooks/useAppConfig';
import { useCatalog } from '../hooks/useCatalog';
import { useLanguages } from '../hooks/useLanguages';
import { fetchTextWithEnglishFallback } from '../services/dataApi';
import { loadTopicLessonsCatalog } from '../services/topicLessons';
import { loadDailyTestsCatalog } from '../services/dailyTests';
import StreakXpBar from '../components/StreakXpBar';
import LearningPathChooser from '../components/LearningPathChooser';
import { LearningProgressService } from '../services/LearningProgressService';
import { SrsService } from '../services/SrsService';

function parseIntro(text) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const title = lines[0] || '';
  const rule = lines.length >= 3 ? lines[lines.length - 1] : '';
  const tipLines = lines.slice(1, -2);
  const tips = [];
  for (let i = 0; i < tipLines.length; i += 2) {
    if (tipLines[i]) tips.push({ heading: tipLines[i], body: tipLines[i + 1] || '' });
  }
  return { title, tips, rule };
}

function useIntro(langCode) {
  const [intro, setIntro] = useState(null);
  useEffect(() => {
    if (!langCode) return;
    setIntro(null);
    fetchTextWithEnglishFallback(
      (locale) => `language-introductions/${locale}/intro.txt`,
      langCode,
    )
      .then((text) => setIntro(parseIntro(text)))
      .catch(() => setIntro(null));
  }, [langCode]);
  return intro;
}

const TIP_ICONS = ['💡', '🔄', '🌍', '📖', '👂', '🗣️', '📅'];

export default function Home() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { languagePrefs, isAuthenticated } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const { languages } = useLanguages();
  const { catalog } = useCatalog();

  const { t } = useTranslation();

  const intro = useIntro(languagePrefs?.nativeLanguage);
  const langMap = Object.fromEntries(languages.map((language) => [language.code, language]));
  const targetCode = languagePrefs?.targetLanguage || '';
  const targetLanguageLabel = langMap[targetCode]?.label || targetCode.toUpperCase() || t('learningPaths.aLanguage');

  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
    : 'guest';
  const [pathProgress, setPathProgress] = useState(() => LearningProgressService.snapshot(langPair));

  useEffect(() => {
    const refresh = () => setPathProgress(LearningProgressService.snapshot(langPair));
    refresh();
    window.addEventListener('learning-progress-changed', refresh);
    return () => window.removeEventListener('learning-progress-changed', refresh);
  }, [langPair]);

  const [dueCount, setDueCount] = useState(0);
  useEffect(() => {
    setDueCount(languagePrefs ? SrsService.getDueCount(langPair) : 0);
  }, [langPair, languagePrefs]);

  const courseLevels = Array.isArray(config.level)
    ? config.level.map((level) => String(level || '').trim().toLowerCase()).filter(Boolean)
    : [];
  const requestedLevel = String(searchParams.get('level') || '').trim().toLowerCase();
  const selectedLevel = courseLevels.includes(requestedLevel) || /^[abc][12]$/.test(requestedLevel)
    ? requestedLevel
    : courseLevels.includes('a1')
      ? 'a1'
      : courseLevels[0] || 'a1';
  const [topicChapters, setTopicChapters] = useState([]);
  const [topicStatus, setTopicStatus] = useState('loading');
  const [dailyTests, setDailyTests] = useState([]);
  const [dailyTestsStatus, setDailyTestsStatus] = useState('loading');

  useEffect(() => {
    if (!targetCode || !selectedLevel) {
      setTopicChapters([]);
      setTopicStatus('unavailable');
      return undefined;
    }
    let active = true;
    setTopicChapters([]);
    setTopicStatus('loading');
    loadTopicLessonsCatalog(targetCode, selectedLevel)
      .then((chapters) => {
        if (!active) return;
        setTopicChapters(chapters);
        setTopicStatus(chapters.length > 0 ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (!active) return;
        setTopicChapters([]);
        setTopicStatus('unavailable');
      });
    return () => { active = false; };
  }, [selectedLevel, targetCode]);

  useEffect(() => {
    if (!targetCode) {
      setDailyTests([]);
      setDailyTestsStatus('unavailable');
      return undefined;
    }
    let active = true;
    setDailyTests([]);
    setDailyTestsStatus('loading');
    loadDailyTestsCatalog(targetCode)
      .then((tests) => {
        if (!active) return;
        setDailyTests(tests);
        setDailyTestsStatus(tests.length > 0 ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (!active) return;
        setDailyTests([]);
        setDailyTestsStatus('unavailable');
      });
    return () => { active = false; };
  }, [targetCode]);

  const targetFeatures = catalog?.[targetCode]?.features;
  const visibleSteps = config.learningSteps.filter((step) => (
    !step.catalogFeature || !targetFeatures || targetFeatures.includes(step.catalogFeature)
  ));

  const startSkillPath = () => navigate('/skill-path');
  const openTopicPath = () => navigate(`/topic-lessons/${selectedLevel}`);

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 12% 12%, #dbeafe 0%, #f8fafc 42%, #ede9fe 100%)' }}>

      <LearningPathChooser
        targetLanguageLabel={targetLanguageLabel}
        level={selectedLevel}
        skillSteps={visibleSteps}
        topicChapters={topicChapters}
        topicStatus={topicStatus}
        dailyTests={dailyTests}
        dailyTestsStatus={dailyTestsStatus}
        onStartSkill={startSkillPath}
        onExploreTopics={openTopicPath}
        onPreviewSkill={startSkillPath}
        onPreviewTopics={openTopicPath}
        onStartDailyTests={() => navigate('/exercises')}
      />

      <main className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">

        <section className="mb-8 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-amber-100 text-2xl" aria-hidden="true">💡</span>
            <div>
              <h2 className="text-base font-black text-slate-900">{t('learningPaths.helpTitle')}</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{t('learningPaths.helpDesc')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={startSkillPath}
            disabled={visibleSteps.length === 0}
            className="shrink-0 rounded-xl border border-blue-300 bg-white px-5 py-2.5 text-sm font-black text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('learningPaths.helpCta')}
          </button>
        </section>

        {/* ── Streak / XP bar (authenticated users only) ── */}
        {isAuthenticated && <StreakXpBar />}

        {/* ── Today's lesson: surfaces SRS due-cards ahead of generic browsing ── */}
        {dueCount > 0 && (
          <section className="mb-8 flex flex-col items-start gap-3 rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-600 to-violet-600 p-5 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-white/70">{t('home.todaysLessonLabel')}</p>
              <h2 className="text-lg font-black">{t('home.dueWordsTitle', { count: dueCount })}</h2>
            </div>
            <button
              onClick={() => navigate('/practice?review=1')}
              className="shrink-0 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-indigo-700 shadow-sm transition hover:-translate-y-0.5"
            >
              {t('home.reviewNow')}
            </button>
          </section>
        )}

        {/* ── Daily mixed-skill mission ── */}
        <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-indigo-600">{t('streakBar.dailyGoal')}</p>
              <h2 className="text-lg font-black text-slate-900">{t('progress.todaysLearning')}</h2>
            </div>
            <span className="text-xs font-bold text-indigo-600">{pathProgress.missions.filter((m) => m.complete).length}/{pathProgress.missions.length} · {t('streakBar.done')}</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {pathProgress.missions.map((mission) => (
              <div key={mission.step} className={`rounded-xl border px-3 py-2 ${mission.complete ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                <p className="text-xs font-bold text-slate-700">{mission.complete ? '✓' : mission.icon} {mission.label}</p>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${(mission.count / mission.goal) * 100}%` }} /></div>
                <p className="mt-1 text-[10px] text-slate-400">{mission.count}/{mission.goal}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Native language tips ── */}
        {intro && (
          <section className="mt-10 rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-6 py-5 md:px-10">
              <p className="text-xs font-black uppercase tracking-widest text-indigo-600">{t('home.tipsLabel')}</p>
              <h2 className="mt-1 text-xl font-black text-slate-900">{intro.title}</h2>
            </div>
            <div className="p-6 md:p-10">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {intro.tips.map((tip, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="mt-0.5 text-xl leading-none shrink-0">{TIP_ICONS[i % TIP_ICONS.length]}</span>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{tip.heading}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{tip.body}</p>
                    </div>
                  </div>
                ))}
              </div>
              {intro.rule && (
                <div className="mt-6 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4">
                  <p className="mb-1 text-xs font-black uppercase tracking-widest text-indigo-500">{t('home.simpleRule')}</p>
                  <p className="text-sm font-semibold text-indigo-900">{intro.rule}</p>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 bg-white/60 py-8 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 flex flex-col items-center gap-3 text-center sm:px-6 lg:px-8">
          <p className="text-xs text-slate-400">
            {t('home.footerText')} · &copy; {new Date().getFullYear()} Subram Miles
          </p>
          <div className="flex flex-wrap justify-center gap-5 text-xs">
            <Link to="/download" className="font-bold text-indigo-500 hover:text-indigo-700 transition">{t('nav.downloadApp')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/terms" className="text-slate-400 hover:text-indigo-600 transition">{t('profile.termsConditions')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/privacy" className="text-slate-400 hover:text-indigo-600 transition">{t('profile.privacyPolicy')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/credits" className="text-slate-400 hover:text-indigo-600 transition">{t('profile.credits')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/contact" className="text-slate-400 hover:text-indigo-600 transition">{t('home.contactUs')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
